// Rest/exhaustion, sleep, and the shelter structure.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// Another subsystem with no assigned step (see world/weather.ts's header
// for why it gets its own file instead of living in main.ts).

import * as THREE from 'three';
import { player } from '@entities/player';
import { cast, endCast } from '@systems/fishing';
import { canStand, onJetty, standY } from '@systems/building';
import { woodCount, spendWood, isNearVillageSite } from '@economy/market';
import { worldToHex, PROP_SCALE } from '@world/terrain';
import { DAY_LEN, gameTimeNow, addGameTime, setGameTime, weatherForDay } from '@world/weather';
import { toonMat, addOutlines } from '@world/toon';
import { buildRoof, thatchMat } from '@world/chunks';

export let rest = 100;
export let wellRestedUntil = -1; // gameTime when the morning buff expires
export let sleepAnim = 0;
// The kind of sleep currently animating (null once sleepAnim runs out) —
// lets the pose system show rough-sleep-specific detail (hat over the
// face, pack and rod set down beside him) without also applying it to
// collapsing from exhaustion or sleeping inside a shelter.
export let sleepQuality: 'collapse' | 'shelter' | 'rough' | null = null;
export const SHELTER_COST = 15;

export interface Shelter {
  x: number;
  z: number;
  mesh: THREE.Group;
}
export let shelter: Shelter | null = null;

export function restSpeedMult(): number {
  if (gameTimeNow() < wellRestedUntil) return 1.12;
  if (rest < 30) return 0.7;
  if (rest < 60) return 0.85;
  return 1;
}
export function restBiteMod(): number {
  if (gameTimeNow() < wellRestedUntil) return 0.1;
  if (rest < 30) return -0.15;
  if (rest < 60) return -0.08;
  return 0;
}
export function nearShelter(): boolean {
  return !!shelter && Math.hypot(shelter.x - player.x, shelter.z - player.z) < 2.5;
}

// Applied once per frame from weather.updateEnvironment's restDrain (being
// awake at night wears him down).
export function applyRestDrain(amount: number): void {
  rest = Math.max(0, rest - amount);
}

// Applied once per frame; ticks down the lie-down animation timer.
export function tickSleepAnim(dt: number): void {
  if (sleepAnim > 0) {
    sleepAnim -= dt;
    if (sleepAnim <= 0) sleepQuality = null;
  }
}

// Cancels any active net cast — kept as a parameter (rather than importing
// systems/fishingNet.ts) purely to avoid a needless extra cross-import;
// the caller already has to know about the net to check net.active anyway.
export function doSleep(quality: 'collapse' | 'shelter' | 'rough', cancelNet: () => void): string {
  if (cast.active) endCast();
  cancelNet();
  sleepQuality = quality;
  const day = Math.floor(gameTimeNow() / DAY_LEN);
  const frac = (gameTimeNow() % DAY_LEN) / DAY_LEN;
  const nightWeather = weatherForDay(day);
  let message: string;
  if (quality === 'collapse') {
    addGameTime(DAY_LEN * 0.28);
    rest = 65;
    message = '💤 He can\'t keep his eyes open — he drops where he stands and sleeps like a stone...';
  } else {
    setGameTime((frac < 0.23 ? day : day + 1) * DAY_LEN + DAY_LEN * 0.23);
    if (quality === 'shelter') {
      rest = 100;
      wellRestedUntil = gameTimeNow() + DAY_LEN * 0.42;
      message = '🛏️ A good night in the shelter — he wakes sharp. ✨';
    } else {
      const wet = nightWeather === 'Rain' || nightWeather === 'Storm';
      rest = wet ? 70 : 85;
      message = wet ? '🛏️ A wet, miserable night in the open...' : '🛏️ He sleeps rough under the stars.';
    }
  }
  sleepAnim = 15 + Math.random() * 5; // a proper night's rest, not a blink
  return message;
}

// A rough shelter, but built to the same grammar as the village dwellings:
// cream plaster panels between dark corner timbers, under a thatched roof
// whose eaves overhang well past the walls. The overhang is what reads as
// Japanese from a distance, more than any detail on the walls.
function buildShelterMesh(): THREE.Group {
  const g = new THREE.Group();
  const wallMat = toonMat(0xd6cdb7);
  const timberMat = toonMat(0x5b4a38);
  const walls = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.44, 0.62), wallMat);
  walls.position.y = 0.22;
  g.add(walls);
  // Posts stop just short of the wall's top face (y=0.44) and the door sits
  // just proud of its face (x=0.36) — flush surfaces z-fight and shimmer.
  ([[0.36, 0.31], [0.36, -0.31], [-0.36, 0.31], [-0.36, -0.31]] as const).forEach(([ox, oz]) => {
    const c = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.41, 0.05), timberMat);
    c.position.set(ox, 0.205, oz);
    g.add(c);
  });
  const sill = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.04, 0.66), timberMat);
  sill.position.y = 0.415;
  g.add(sill);
  g.add(buildRoof(1.04, 0.92, 0.45, thatchMat));
  const door = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.3, 0.22), timberMat);
  door.position.set(0.378, 0.15, 0);
  g.add(door);
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

export function buildShelter(): { success: boolean; reason?: string } {
  if (woodCount() < SHELTER_COST) return { success: false, reason: 'not-enough-wood' };
  const tx = player.x + Math.cos(player.heading) * 1.15;
  const tz = player.z + Math.sin(player.heading) * 1.15;
  if (!canStand(tx, tz) || onJetty(tx, tz)) return { success: false, reason: 'no-room' };
  // Keep clear of the village: its market stall, drying rack and dwelling
  // are one rigid group spanning well past the hex it's sited on, so a
  // shelter dropped nearby would land inside the buildings.
  const hx = worldToHex(tx, tz);
  if (isNearVillageSite(hx.q, hx.r, 2, gameTimeNow())) return { success: false, reason: 'too-close-to-market' };
  const g = buildShelterMesh();
  g.position.set(tx, standY(tx, tz), tz);
  g.rotation.y = -player.heading;
  shelter = { x: tx, z: tz, mesh: g };
  spendWood(SHELTER_COST);
  return { success: true };
}

// Builds a non-interactive shelter mesh for a remote player's shelter at a
// given world position — used by entities/remotePlayers.ts, which owns
// adding it to the scene/group and tracking it per player id.
export function addRemoteShelterMesh(x: number, z: number, heading: number): THREE.Group {
  const g = buildShelterMesh();
  g.position.set(x, standY(x, z), z);
  g.rotation.y = -heading;
  return g;
}
